import { useEffect, useMemo, useState } from "react";
import { Control, FieldError, FieldValues } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AmountInput,
  CounterParametersInputFields,
  IbanInput,
  InputGroup,
  Loader,
  ParameterInput,
} from "@monorepo/components";
import { findErrorByParam } from "@monorepo/helpers";
import theme from "@monorepo/styles/theme";
import {
  AmountCalculationEnum,
  IResponseError,
  IServiceParameter,
  ParameterTypeEnum,
  ServiceParameterEnum,
} from "@monorepo/types";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";

type TParametersInputFieldsProps<T extends FieldValues> = {
  parameters: Array<IServiceParameter>;
  inputErrors: Array<IResponseError>;
  amtCalculable?: AmountCalculationEnum;
  onCalculate?: () => void;
  amount?: number;
  calculateError?: FieldError;
  control: Control<T>;
  filterCountersByGroupLabelOnly?: boolean;
  isCalculateLoading: boolean;
  isThirdPartyPE?: boolean;
};

export function ParametersInputFields<T extends FieldValues>({
  amount,
  amtCalculable,
  parameters,
  inputErrors,
  onCalculate,
  calculateError,
  control,
  filterCountersByGroupLabelOnly = false,
  isCalculateLoading,
  isThirdPartyPE,
}: TParametersInputFieldsProps<T>) {
  const { t } = useTranslation();
  const { register } = useFormContext();
  const [params, setParams] = useState(parameters);

  useEffect(() => {
    if (JSON.stringify(params) !== JSON.stringify(parameters)) {
      setParams(parameters);
    }
  }, [params, parameters]);

  const filteredCounterParams = useMemo(() => {
    return params?.filter((param) =>
      filterCountersByGroupLabelOnly ? param.group_label : param.group_label && param.meter_type
    );
  }, [params, filterCountersByGroupLabelOnly]);

  return (
    <>
      {params?.map((parameter, index) => {
        const parameterName = parameter.parameter_name;
        // hide a hidden input (not a counter input though- it will be rendered hidden by CounterParametersInputFields)
        if (parameter.hidden && !parameter.group_label) {
          return (
            <input
              key={parameterName}
              {...register(parameter.parameter_name)}
              hidden
              name={parameter.parameter_name}
              defaultValue={parameter.value}
            />
          );
        }
        if (
          parameter.parameter_type === ParameterTypeEnum.CURRENCY &&
          (parameter.parameter_name === ServiceParameterEnum.AMT ||
            parameter.parameter_name.replace(/.$/, "") === ServiceParameterEnum.AMT)
        ) {
          if (amtCalculable !== AmountCalculationEnum.CALCULABLE) {
            return (
              <AmountInput
                label={parameter.api_parameter_label}
                responseError={findErrorByParam({ param: parameterName, error: inputErrors })}
                control={control}
                required={false}
                key={parameter.parameter_name}
                registerName={parameter.parameter_name}
                readOnly={parameter.read_only}
                disabled={parameter.read_only}
                defaultValue={parameter.value || "0.00"}
              />
            );
          } else if (amtCalculable === AmountCalculationEnum.CALCULABLE && onCalculate) {
            return (
              <InputGroup key={parameter.parameter_name}>
                <Box
                  key={parameter.parameter_name}
                  sx={{
                    marginTop: theme.spacing(1),
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    width: "100%",
                    columnGap: theme.spacing(2),
                  }}
                >
                  <AmountInput
                    isCalculateLoading={isCalculateLoading}
                    control={control}
                    registerName={parameter.parameter_name}
                    responseError={findErrorByParam({ param: parameterName, error: inputErrors })}
                    disabled={parameter.read_only}
                    defaultValue={amount ? String(amount) : parameter.value}
                  />
                  {isThirdPartyPE && !!isCalculateLoading && <Loader />}
                  {!isThirdPartyPE && (
                    <LoadingButton
                      onClick={onCalculate}
                      sx={{
                        borderRadius: "50px",
                        padding: {
                          xs: theme.spacing(1, 3),
                        },
                      }}
                      variant="contained"
                      disableElevation
                      color="primary"
                      loading={!!isCalculateLoading}
                    >
                      {t("payments.calculate")}
                    </LoadingButton>
                  )}
                </Box>
              </InputGroup>
            );
          }
        }
        if (parameter.parameter_type === ParameterTypeEnum.IBAN) {
          return (
            <IbanInput
              control={control}
              key={parameter.parameter_name}
              registerName={parameter.parameter_name}
              disabled={parameter.read_only}
              defaultValue={{ value: parameter.value }}
              responseError={findErrorByParam({ param: parameterName, error: inputErrors })}
            />
          );
        }

        // showing counters only for first element with group_label
        if (parameter.group_label) {
          if (index === params.findIndex((p) => p.group_label)) {
            return (
              <CounterParametersInputFields
                key={
                  parameter.group_label +
                  parameter.parameter_name +
                  parameter.service_parameter_id +
                  parameter.meter_type
                }
                control={control}
                isThirdPartyPE={isThirdPartyPE}
                onCalculate={onCalculate}
                parameters={filteredCounterParams}
                inputErrors={inputErrors}
                calculateError={calculateError?.message}
              />
            );
          }
          return null;
        }

        return (
          <ParameterInput
            key={parameterName}
            parameter={parameter}
            isThirdPartyPE={isThirdPartyPE}
            error={findErrorByParam({ param: parameterName, error: inputErrors })}
          />
        );
      })}
    </>
  );
}
